import { useMemo, useState } from 'react'
import { useApp } from '../state/AppStore'
import { useLiveGame } from '../state/useLiveGame'
import { useGameSync } from '../state/useGameSync'
import { useWakeLock } from '../lib/useWakeLock'
import { orderStatTypes } from '../domain/statTypes'
import { periodLabel, periodList } from '../domain/periods'
import { teamTotal } from '../domain/derive'
import { formatDate } from '../lib/date'
import { StatGrid } from '../components/StatGrid'
import { EventLog } from './EventLog'
import { EndGame } from './EndGame'
import { ExitGame } from './ExitGame'
import { LiveView } from './LiveView'
import { liveEvents } from '../domain/derive'
import { useAuth } from '../auth/AuthProvider'
import type { ID } from '../domain/types'

/**
 * The screen that runs during the game. One row per present player, one column
 * per enabled stat, every button showing that player's running total. Fixed
 * height, never scrolls: the row track shrinks to fit up to a 10-player roster.
 *
 * Step 2 adds a second device to the picture without changing that: taps still
 * land in IndexedDB first and are drawn immediately, and the sync layer carries
 * them up afterwards. The only thing that stops this screen accepting taps is
 * being told, by the server, that somebody else has taken over -- at which point
 * it becomes the read-only view, because from then on this copy would be a fork.
 */
export function LiveGame({ gameId, onEnded }: { gameId: ID; onEnded: () => void }) {
  const { players, opponents, currentSeason, deleteGame, setCurrentGameId } = useApp()
  const live = useLiveGame(gameId)
  const { member } = useAuth()
  const [overlay, setOverlay] = useState<'none' | 'log' | 'end' | 'exit'>('none')

  useWakeLock(overlay === 'none')

  const stats = useMemo(() => orderStatTypes(currentSeason.enabledStatTypes), [currentSeason.enabledStatTypes])

  const { game, counts, events } = live

  const sync = useGameSync({
    game,
    events,
    players,
    onSynced: live.markSynced,
    currentPeriod: game?.currentPeriod ?? 1,
    periodFormat: currentSeason.periodFormat,
    enabledStatTypes: currentSeason.enabledStatTypes,
  })

  const roster = useMemo(() => {
    if (!game) return []
    // Preserve roster order, and keep a placeholder if a player was archived mid-season.
    return game.roster.map((id) => players.find((p) => p.id === id) ?? { id, name: 'Unknown', active: false })
  }, [game, players])

  const totals = useMemo(() => new Map(stats.map((id) => [id, teamTotal(events, id)] as const)), [stats, events])

  const canScore = member?.role === 'scorer' || member?.role === 'admin'

  if (!game) return <div className="boot">Game not found.</div>

  const recordedCount = liveEvents(live.events).length

  /**
   * Somebody else holds the pen. Drop to the read-only view rather than let this
   * device keep tapping into a copy that no longer goes anywhere -- the brief's
   * "the previous device drops to read-only", and the honest rendering of a
   * device whose writes the server is refusing.
   */
  if (sync.pen === 'theirs') {
    return (
      <LiveView
        gameId={game.id}
        holderName={sync.holderName}
        canScore={canScore}
        onTakeOver={canScore ? () => void takeBack() : undefined}
        onLeave={() => void keepAsDraft()}
      />
    )
  }

  async function takeBack() {
    await sync.takeOver()
    // Whatever the other device recorded while it held the pen belongs in this
    // scoresheet now, or the totals here would be missing a period.
    const remote = await sync.pullAll()
    await live.mergeRemote(remote)
  }

  /**
   * Leaving without ending. An empty game is closed and deleted on the spot --
   * there is nothing to lose and a confirmation would just be noise. Anything
   * with taps in it asks first.
   */
  const handleExit = async () => {
    if (!live.game) return
    if (recordedCount === 0) {
      await sync.release('manual')
      await setCurrentGameId(null)
      await deleteGame(live.game)
      onEnded()
      return
    }
    setOverlay('exit')
  }

  async function keepAsDraft() {
    // Stays `in_progress`, so it shows up as a resumable draft on the setup
    // screen. The claim is released so the next person can pick the game up.
    await sync.release('manual')
    await setCurrentGameId(null)
    onEnded()
  }

  const discard = async () => {
    if (!live.game) return
    await sync.release('manual')
    await setCurrentGameId(null)
    await deleteGame(live.game)
    onEnded()
  }

  const opponentName =
    game.opponentName ?? opponents.find((o) => o.id === game.opponentId)?.name ?? 'Opponent'
  const periods = periodList(currentSeason.periodFormat)

  return (
    <div className="live">
      <header className="live__header">
        {/* Far corner, diagonally opposite UNDO and clear of the grid: leaving is
            never something to hit by accident mid-game. */}
        <button type="button" className="live__exit" onClick={handleExit} aria-label="Leave this game">
          ×
        </button>
        <div className="live__match">
          <span className="live__vs">{game.homeAway === 'home' ? 'vs' : '@'}</span>
          <span className="live__opponent">{opponentName}</span>
          <span className="live__date">
            {game.roundAbbreviation ? `${game.roundAbbreviation} · ` : ''}
            {formatDate(game.date)}
            {game.courtName ? ` · ${game.courtName}` : ''}
          </span>
        </div>

        <div className="seg seg--period">
          {periods.map((p) => (
            <button
              key={p}
              type="button"
              className={'seg__btn' + (game.currentPeriod === p ? ' seg__btn--on' : '')}
              onClick={() => live.setPeriod(p)}
            >
              {periodLabel(currentSeason.periodFormat, p)}
            </button>
          ))}
        </div>

        <div className="live__actions">
          <SyncPip sync={sync} />
          <button type="button" className="btn btn--undo" onClick={live.undo}>
            UNDO
          </button>
          <button type="button" className="btn" onClick={() => setOverlay('log')}>
            Log
          </button>
          <button type="button" className="btn btn--end" onClick={() => setOverlay('end')}>
            End
          </button>
        </div>
      </header>

      {live.saveError && (
        <div className="banner banner--error">
          Storage write failed: {live.saveError}. Recent taps may not be saved.
        </div>
      )}

      {sync.error && sync.pen !== 'offline' && (
        <div className="banner banner--error">
          {sync.error} Your taps are saved on this device.
        </div>
      )}

      <StatGrid rows={roster} stats={stats} counts={counts} totals={totals} onTap={live.record} />

      {overlay === 'log' && (
        <EventLog
          events={events}
          roster={roster}
          periodFormat={currentSeason.periodFormat}
          onDelete={live.deleteEvent}
          onReassign={live.reassignEvent}
          onClose={() => setOverlay('none')}
        />
      )}

      {overlay === 'exit' && live.game && (
        <ExitGame
          game={live.game}
          eventCount={recordedCount}
          opponentName={opponentName}
          onKeepDraft={() => void keepAsDraft()}
          onDiscard={() => void discard()}
          onCancel={() => setOverlay('none')}
        />
      )}

      {overlay === 'end' && (
        <EndGame
          game={game}
          opponentName={opponentName}
          onCancel={() => setOverlay('none')}
          onEnded={() => {
            void sync.release('ended')
            onEnded()
          }}
        />
      )}
    </div>
  )
}

/**
 * A single character of status, because the header has no room for a sentence
 * and the scorer has no attention to spare for one. It still has to be honest:
 * "everything is up" and "eleven taps are stuck on this iPad" must not look the
 * same, which is the whole lesson of this project.
 */
/**
 * Whether it is safe to close the app.
 *
 * A scorer at pack-up time needs one thing from this: has every tap reached the
 * server, or is some of it still only on this iPad? So the indicator says that
 * in words -- "Saved" or "N not saved" with a coloured dot -- rather than a terse
 * code the teammate would have to have been taught. Green means safe to close;
 * anything else means keep the app open (and get online).
 */
function SyncPip({ sync }: { sync: ReturnType<typeof useGameSync> }) {
  if (sync.pen === 'not-syncable') {
    return (
      <span className="pip pip--local" title="A game started by hand, with no PlayHQ fixture. It stays on this iPad; nothing to send.">
        <span className="pip__dot" />
        On device
      </span>
    )
  }
  if (sync.pen === 'checking') {
    return (
      <span className="pip" title="Checking the connection…">
        <span className="pip__dot" />
        …
      </span>
    )
  }
  // Offline takes priority: the danger is unsaved taps, whatever the count.
  if (sync.pen === 'offline') {
    return (
      <span
        className="pip pip--offline"
        title={
          (sync.pending > 0
            ? `${sync.pending} tap(s) are saved on this iPad only. Get back online before closing the app.`
            : 'No connection. Scoring still works; get online before closing.') + (sync.error ? ` (${sync.error})` : '')
        }
      >
        <span className="pip__dot" />
        Offline{sync.pending > 0 ? ` · ${sync.pending} unsaved` : ''}
      </span>
    )
  }
  if (sync.pending > 0) {
    return (
      <span className="pip pip--queued" title={`${sync.pending} tap(s) still uploading. Keep the app open a moment.`}>
        <span className="pip__dot" />
        {sync.pending} to save
      </span>
    )
  }
  // Online and nothing waiting: everything recorded is on the server.
  return (
    <span className="pip pip--live" title="Every tap is on the server. Safe to close the app.">
      <span className="pip__dot" />
      Saved
    </span>
  )
}
